/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import downloadjs from 'downloadjs';
import { QSIconButton } from '../../../../../shared/components/atoms';
import { MessageTypesEnum } from '../../typings/message-types';
import ReactDOM from 'react-dom';
import { useAtom, useAtomValue } from 'jotai';
import { mediaViewerAtomV2 } from '../../states/media-viewer-state';
import { Button, ClickAwayListener } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PDFIcon from '../../../../../shared/icons/PDF';
import MediaViewerCloseIcon from '../../../../../shared/icons/MediaViewerClose';
import MediaViewerDownloadIcon from '../../../../../shared/icons/MediaViewerDownload';
import MediaViewerZoomInIcon from '../../../../../shared/icons/MediaViewerZoomIn';
import MediaViewerZoomOutIcon from '../../../../../shared/icons/MediaViewerZoomOut';
import MediaViewerForwardIcon from '../../../../../shared/icons/MediaViewerForwardIcon';
import { useGetToken } from '../../../../../shared/hooks/useGetToken';
import { getUrlWithToken } from '../../../../../shared/utils/getMediaURLWithToken';
import forwardMsgAtom, { forwardMsgReducer } from '../../../../forward-msg-popup/state';
import { ForwardMsgAction } from '../../../../forward-msg-popup/types';
import { useReducerAtom } from 'jotai/utils';

export const MediaViewerV2 = () => {
  const [state, dispatch] = useAtom(mediaViewerAtomV2);
  const [, forwardMsgDispatch] = useReducerAtom(forwardMsgAtom, forwardMsgReducer);
  const forwardMsgState = useAtomValue(forwardMsgAtom);

  const types = state.messageTypes;
  const [zoomScale, setZoomScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const panStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const didDragRef = useRef(false);
  const portalContainer = document.getElementById('portal')!;
  const userToken = useGetToken();

  const isZoomableMedia = types[state.activeIndex] === MessageTypesEnum.IMAGE;
  const canZoomOut = zoomScale > 1;
  const canZoomIn = zoomScale < 3;

  const handlePrevious = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    setZoomScale(1);
    setPan({ x: 0, y: 0 });
    didDragRef.current = false;
    if (state.activeIndex > 0) {
      dispatch({
        ...state,
        activeIndex: state.activeIndex - 1,
      });
    }
  };

  const handleNext = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    setZoomScale(1);
    setPan({ x: 0, y: 0 });
    didDragRef.current = false;
    if (state.activeIndex < state.filesUrl.length - 1) {
      dispatch({
        ...state,
        activeIndex: state.activeIndex + 1,
      });
    }
  };

  const handleZoomIn = (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canZoomIn) return;
    setZoomScale((prev) => {
      const next = prev < 1 ? 1 : prev;
      if (next >= 3) return 3;
      if (next < 1.5) return 1.5;
      if (next < 2) return 2;
      return 3;
    });
  };

  const handleZoomOut = (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canZoomOut) return;
    setZoomScale((prev) => {
      const next = prev < 1 ? 1 : prev;
      if (next <= 1) return 1;
      if (next <= 1.5) return 1;
      if (next <= 2) return 1.5;
      return 2;
    });
  };

  const startDragPan = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (zoomScale <= 1) return;
      event.preventDefault();
      event.stopPropagation();
      didDragRef.current = false;
      dragStartRef.current = { x: event.clientX, y: event.clientY };
      panStartRef.current = pan;
      setIsDragging(true);
    },
    [pan, zoomScale]
  );

  useEffect(() => {
    if (!isDragging) return;

    const thresholdPx = 4;

    const onMouseMove = (event: MouseEvent) => {
      if (!dragStartRef.current) return;
      event.preventDefault();
      const dx = event.clientX - dragStartRef.current.x;
      const dy = event.clientY - dragStartRef.current.y;
      if (!didDragRef.current && (Math.abs(dx) > thresholdPx || Math.abs(dy) > thresholdPx)) {
        didDragRef.current = true;
      }
      setPan({
        x: panStartRef.current.x + dx,
        y: panStartRef.current.y + dy,
      });
    };

    const onMouseUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging]);

  useEffect(() => {
    if (zoomScale === 1) {
      setPan({ x: 0, y: 0 });
      setIsDragging(false);
      dragStartRef.current = null;
      didDragRef.current = false;
    }
  }, [zoomScale]);

  const fileNameAndExtension = useMemo(() => {
    if (!state.filesUrl[state.activeIndex]) return { name: '', extension: '' };
    const fileName = state.filesUrl[state.activeIndex].substring(
      state.filesUrl[state.activeIndex].lastIndexOf('/') + 1
    );
    if (fileName.includes('.')) {
      const name = fileName.split('.').slice(0, -1).join('.');
      const extension = fileName.split('.').pop();
      return { name, extension };
    } else {
      return { name: fileName, extension: '' };
    }
  }, [state.filesUrl]);

  const getFileExtensionFromMimeType = (mimeType: string): string => {
    const mimeTypesMap: Record<string, string> = {
      // Document MIME types
      'text/plain': 'txt',
      'application/pdf': 'pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/msword': 'doc',
      'application/vnd.ms-powerpoint': 'ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',

      // Image MIME types
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/bmp': 'bmp',
      'image/tiff': 'tiff',
      'image/svg+xml': 'svg',

      // Video MIME types
      'video/mp4': 'mp4',
      'video/mpeg': 'mpeg',
      'video/ogg': 'ogv',
      'video/webm': 'webm',
      'video/quicktime': 'mov',
      'video/x-msvideo': 'avi',
      'video/x-ms-wmv': 'wmv',
      'video/3gp': '3gp',

      // Audio MIME types
      'audio/aac': 'aac',
      'audio/mpeg': 'mp3',
      'audio/mp4': 'm4a',
      'audio/amr': 'amr',
      'audio/ogg': 'ogg',
      'audio/ogg; codecs=opus': 'opus',
    };

    return mimeTypesMap[mimeType] || '';
  };

  const handleMediaDownload = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      let urlWithQueryParams = getUrlWithToken(state.filesUrl[state.activeIndex], userToken ?? '');
      fetch(urlWithQueryParams, {
        method: 'GET',
        headers: {},
      })
        .then((response) => {
          response.arrayBuffer().then(function (buffer) {
            const url = window.URL.createObjectURL(new Blob([buffer]));
            const contentType = response.headers.get('content-type');
            const extension =
              fileNameAndExtension.extension === '' || fileNameAndExtension.extension === 'null'
                ? getFileExtensionFromMimeType(contentType ?? '')
                : fileNameAndExtension.extension;
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${fileNameAndExtension.name}.${extension}`);
            document.body.appendChild(link);
            link.click();
          });
        })
        .catch(() => {
          downloadjs(
            state.filesUrl[state.activeIndex],
            `${fileNameAndExtension.name}.${fileNameAndExtension.extension}`,
            'image/png'
          );
        });
    },
    [fileNameAndExtension.extension, fileNameAndExtension.name, state.filesUrl]
  );

  const handleClose = () => {
    setZoomScale(1);
    setPan({ x: 0, y: 0 });
    setIsDragging(false);
    dragStartRef.current = null;
    didDragRef.current = false;
    dispatch({
      isMediaViewerV2Active: false,
      messageTypes: [MessageTypesEnum.IMAGE],
      filesUrl: [],
      forwardPayloads: [],
      activeIndex: 0,
    });
  };

  const activeForwardPayload = state.forwardPayloads[state.activeIndex];
  const canForward = !!activeForwardPayload?.msg && !!activeForwardPayload?.msgId;

  const handleForwardMessage = (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canForward || !activeForwardPayload) return;
    forwardMsgDispatch({
      type: ForwardMsgAction.SHOW_MSGS_SELECT_VIEW,
      data: {
        msg: activeForwardPayload.msg,
        msgId: activeForwardPayload.msgId,
      },
    });
    forwardMsgDispatch({
      type: ForwardMsgAction.SHOW_MSGS_FORWARD_POPUP,
      data: {
        fromMediaViewer: true,
      },
    });
  };

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [dispatch, handleClose]);

  return ReactDOM.createPortal(
    <div
      id="media-viewer-container"
      css={css`
        height: 100vh;
        width: 100vw;
        z-index: 99999;
        position: absolute;
        background: rgba(17, 27, 33, 1);
        top: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: flex-end;
          width: 100%;
          position: relative;
          z-index: 20;
          background: rgba(17, 27, 33, 1);
          margin-bottom: 0;
          padding: 12px 24px;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 4px;
          `}
        >
          <QSIconButton
            sx={{
              background: 'none',
            }}
            onClick={handleMediaDownload}
          >
            <MediaViewerDownloadIcon />
          </QSIconButton>
          {isZoomableMedia ? (
            <>
              <QSIconButton
                sx={{
                  background: 'none',
                  opacity: canZoomOut ? 1 : 0.4,
                  cursor: canZoomOut ? 'pointer' : 'not-allowed',
                }}
                onClick={handleZoomOut}
              >
                <MediaViewerZoomOutIcon />
              </QSIconButton>
              <QSIconButton
                sx={{
                  background: 'none',
                  opacity: canZoomIn ? 1 : 0.4,
                  cursor: canZoomIn ? 'pointer' : 'not-allowed',
                }}
                onClick={handleZoomIn}
              >
                <MediaViewerZoomInIcon />
              </QSIconButton>
              <QSIconButton
                sx={{
                  background: 'none',
                  opacity: canForward ? 1 : 0.4,
                  cursor: canForward ? 'pointer' : 'not-allowed',
                }}
                onClick={handleForwardMessage}
              >
                <MediaViewerForwardIcon />
              </QSIconButton>
            </>
          ) : null}
          <QSIconButton
            sx={{
              background: 'none',
            }}
            onClick={handleClose}
          >
            <MediaViewerCloseIcon />
          </QSIconButton>
        </div>
      </div>

      <div
        css={css`
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          z-index: 10;
          padding: 24px;
          padding-top: 0;
        `}
      >
        <ClickAwayListener
          onClickAway={() => {
            if (forwardMsgState.showMsgsForwardPopup) {
              return;
            }
            if (didDragRef.current) {
              didDragRef.current = false;
              return;
            }
            handleClose();
          }}
        >
          <div
            css={css`
              display: flex;
              transition: transform 0.3s ease-in-out;
              transform: translateX(-${state.activeIndex * 100}%);
              width: ${state.filesUrl.length * 100}%;
              height: 100%;
            `}
          >
            {state.filesUrl.map((url, index) => (
              <div
                key={url}
                css={css`
                  min-width: 100%;
                  height: 100%;
                  max-height: 90%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0 20px;
                `}
              >
                {types[index] === MessageTypesEnum.IMAGE &&
                  (index === state.activeIndex ? (
                    <div
                      onMouseDown={startDragPan}
                      css={css`
                        transform: translate3d(${pan.x}px, ${pan.y}px, 0);
                        cursor: ${zoomScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'};
                      `}
                    >
                      <img
                        src={url}
                        alt=""
                        css={css`
                          max-width: 100%;
                          max-height: 90%;
                          object-fit: contain;
                          transition: transform 0.25s ease;
                          transform: scale(${zoomScale});
                          transform-origin: 50% 50%;
                          user-select: none;
                          -webkit-user-drag: none;
                        `}
                        draggable={false}
                      />
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt=""
                      css={css`
                        max-width: 100%;
                        max-height: 90%;
                        object-fit: contain;
                      `}
                    />
                  ))}
                {types[index] === MessageTypesEnum.VIDEO && (
                  <video
                    src={url}
                    controls
                    css={css`
                      max-width: 100%;
                      max-height: 90%;
                      object-fit: contain;
                    `}
                  ></video>
                )}
                {types[index] === MessageTypesEnum.AUDIO && <audio src={url} controls />}
                {types[index] === MessageTypesEnum.DOCUMENT && <PDFIcon style={{ width: '60%', height: '60%' }} />}
              </div>
            ))}
          </div>
        </ClickAwayListener>
        {/* Left Arrow */}
        {state.activeIndex > 0 && (
          <Button
            onClick={handlePrevious}
            css={css`
              position: absolute;
              left: 20px;
              top: 50%;
              transform: translateY(-50%);
              background: rgba(0, 0, 0, 0.5);
              border: none;
              height: 48px;
              width: 48px;
              min-width: 48px;
              color: #fff;
              border-radius: 50%;
              z-index: 2;
              cursor: pointer;
              &:hover {
                background: rgba(0, 0, 0, 0.7);
              }
            `}
          >
            <ArrowBackIcon />
          </Button>
        )}
        {/* Right Arrow */}
        {state.activeIndex < state.filesUrl.length - 1 && (
          <Button
            onClick={handleNext}
            css={css`
              position: absolute;
              right: 20px;
              top: 50%;
              transform: translateY(-50%);
              background: rgba(0, 0, 0, 0.5);
              border: none;
              height: 48px;
              width: 48px;
              min-width: 48px;
              color: #fff;
              border-radius: 50%;
              z-index: 2;
              cursor: pointer;
              &:hover {
                background: rgba(0, 0, 0, 0.7);
              }
            `}
          >
            <ArrowForwardIcon />
          </Button>
        )}
      </div>
    </div>,
    portalContainer
  );
};
